import _isEmpty from 'lodash/isEmpty';
import { purgeStoredState } from 'redux-persist';
import { asyncSessionStorage } from 'redux-persist/storages';

import { locationUpdate, redirectLocation } from './action-contact-location';
import { contactByEmail } from './action-contact-option';
import { fetchFinalTopicContent } from './action-select-topic';
import Page from '../../lib/page';
import { modalChannels } from '../constants/UtilVariables';
import { closeModalContainer, loadMessageModal } from './action-login-modal';
import l10n from '../../lib/reactPPl10nHelper';
import { trackPage } from '../../lib/analytics-helper';
import { initiateRiskCheck, isLoggedOutToLogin, resetStepUp } from './action-step-up';
import getOpenDisputes from '../../home/actions/action-dispute-transactions';
import { userSelectionPostRequest } from './action-update-user-selection';
import { isFinalIntent } from '../../lib/step-up-helper';
import {
  resetAllChatClicksInSession,
  setChatCardClickInSession,
  wasChatBotClicked,
} from '../../lib/chat-bot-live-helper';
import { isCallUsPartOfIdi, isSuppressCallUs } from '../../lib/covidHelper';
import { showHideCovidBcpModalAlert } from './action-covid-modal-alert';
import { asyncChatResolver, chatBotEligibilityResponse } from './action-chat-bot';
import { isC2MerchantMigrated } from '../../lib/globalHelper';
import { resetOcpContextResponse, updateContext } from './action-article';
import { getChatBotEligibilityParams, isNewContactUsExpEnabled } from '../../../jsV2/helpers/twHelper';
import { fetchTopicData } from '../../../jsV2/components/ContactOption/ContactOptionsAction';
import { intents, intentType, pageNames } from '../../../jsV2/common/constants';
import { postChatBotEligibility } from './action-postchatbot-eligibility';
import { finalIntentSelected } from '../../../jsV2/components/topicIntentSection/TopicIntents-Actions';
import { asyncOrigins } from '../../common/commonConstants';

const modalErrorMsg = l10n.getContent('smarthelp/info.info.title.functionIsUnavailable');

const isLoggedIn = Page.isLoggedIn();

/**
 * Render EMAIL page.
 * If User is SMC customer redirect to smc/customer
 * If User is Non-SMC open smart help email form.
 * @param fields
 * @param topicTreeSelectResult
 * @param items
 * @returns {Function}
 */
const renderEmailView = (fields, topicTreeSelectResult, items) => (dispatch) => {
  if (Page.isRedirectToSMC()) {
    setTimeout(() => purgeStoredState({ storage: asyncSessionStorage }).then(() => {}), 400);
    return window.open(
      `/smc/compose?topicId=${topicTreeSelectResult.topicId}&breadcrumbs=${items.toString()}`,
      '_self',
    );
  }
  dispatch(locationUpdate({ pageName: 'contactUs' }));
  return dispatch(contactByEmail(fields));
};
/**
 *  FINAL INTENT LOGGED OUT to LOGGED IN EMAIL CONTACT OPTION
 *  During the final intent Email option from loggedout to loggedin call IDI contact options before making the decision.
 *  If IDI return email, then dont check for pilotUser and execute the email flow.
 *  If IDI does not return email then check messageUs contact option is available and if available open message Us.
 * @returns {Function}
 */
const renderFinalIntentEmailFlow = () => (dispatch, getState) => {
  const {
    modal: { fromChannel },
    persistEmailForm: { kanaCode, fields },
    smcResultKeeper: { topicTreeSelectResult },
    breadCrumb: { items },
  } = getState();

  // IDI service call - Get IDI contact options for the selected topic in loggedOut state.
  const getIDIContactOptions = userSelectionPostRequest(
    kanaCode,
    topicTreeSelectResult && topicTreeSelectResult.topicName,
  );

  getIDIContactOptions.then((response) => {
    const contactChannels = (response.data && response.data.channels) || [];

    if (Array.isArray(contactChannels) && contactChannels.length) {
      // Check if EMAIL options exists in contact channel
      // If exists and if SMC enabled customer, redirect to SMC page
      // If exists and Non SMC customer, redirect to smart help email form view.
      const isEmailChannelAvailable = contactChannels.find((channel) => channel.name === 'EMAIL');

      if (typeof isEmailChannelAvailable === 'object') {
        if (!Page.isEmailNotSent()) {
          return dispatch(renderEmailView(fields, topicTreeSelectResult, items));
        }
        return null;
      }

      // Check if messageUs channel is available
      // If exists render final intent and open messageUs window.
      const isMessageUsChannelAvailable = contactChannels.find((channel) => channel.name === 'MESSAGE_US');

      if (typeof isMessageUsChannelAvailable === 'object') {
        // making chat card clicked true after pilot user clicks on email icon and log in.
        setChatCardClickInSession(true);
        dispatch(fetchFinalTopicContent({ topic: topicTreeSelectResult, fromChannel })); // show last final intent when logout to login in the final intent
        return asyncChatResolver(`${topicTreeSelectResult.topicRefId}|finalIntent`);
      }
    }

    // Default fallback.
    return dispatch(renderEmailView(fields, topicTreeSelectResult, items));
  });

  // On service fail fall back to default logic.
  getIDIContactOptions.catch(() => dispatch(renderEmailView(fields, topicTreeSelectResult, items)));
};

export default function fetchContactUsOnLogin() {
  // eslint-disable-next-line consistent-return
  return (dispatch, getState) => {
    const {
      modal: { fromChannel, topic, endPoint, fromComplaintsPage, fromEmailPage },
      user: { islocaleChanged },
      smcResultKeeper: { topicTreeSelectResult },
      persistLocation: { pageName },
      persistedStepUp: {
        fromChannel: stepUpFromChannel,
        topic: persistedTopic,
        anw: { isRedirectedToAnw },
        stepUpRequestedTime,
        riskInfo,
      },
      contactChannels: { channels },
      ocpContextResponse: { setContextResponse, setContextIntents, faq },
    } = getState();

    // if user login by clicking call us button in contact us page
    if (fromChannel === modalChannels.call && isLoggedIn) {
      dispatch(redirectLocation({ pageName: pageNames.CALL_US }));
    }
    // if final intent is selected before user login
    if (!_isEmpty(topic) || !_isEmpty(persistedTopic)) {
      let selectedIntent = { ...persistedTopic };
      if (_isEmpty(persistedTopic)) selectedIntent = { ...topic };

      const selectedFinalIntent = {
        ...selectedIntent,
        ref_id: selectedIntent.topicRefId,
        topic_name: selectedIntent.topicName,
        topic_id: selectedIntent.topicId,
      };

      dispatch(finalIntentSelected(selectedFinalIntent));
      dispatch(fetchTopicData(selectedFinalIntent.topic_id));
      const postChatBotResponse = getChatBotEligibilityParams(selectedFinalIntent.ref_id, intentType.TOPIC);
      dispatch(postChatBotEligibility(postChatBotResponse));
    } else if (_isEmpty(topic)) {
      const postChatBotResponse = getChatBotEligibilityParams(intents.CONTACT_US_PAGE, intentType.GENERIC);
      dispatch(postChatBotEligibility(postChatBotResponse));
    }

    // opening callus page when redirected from complaints page after clicking on callus icon
    if (fromComplaintsPage) {
      return dispatch(locationUpdate({ pageName: 'callUs' }));
    }

    if (Page.isLoggedIn()) {
      /**
       * If user is c2merchant migrated land user in contact-us default page
       */
      if (isC2MerchantMigrated()) {
        // resetting chat clicks
        return resetAllChatClicksInSession();
      }

      // Dispatching action for pending disputes only in logged-in scenario to show disputes alert
      if (!isNewContactUsExpEnabled()) {
        dispatch(getOpenDisputes());
      }

      // If isRedirectedToAnw[redux state value] is true then perform step-up
      // This will be when user clicks on back button in ANW page or when redirected from ANW with success or error.
      /* eslint-disable array-callback-return */
      if (isRedirectedToAnw) {
        let setContextReqIntent;
        let contextId;
        const topicRefId = topicTreeSelectResult.topicRefId;

        // If the set context Request has more than one intent, make the update context call for each intent after step up authentication
        if (topicRefId && riskInfo && riskInfo.authflow_context_id && stepUpRequestedTime && faq) {
          if (
            Array.isArray(setContextIntents) &&
            setContextIntents.length > 1 &&
            Array.isArray(setContextResponse) &&
            setContextResponse.length > 1
          ) {
            setContextIntents.map((intent, index) => {
              setContextReqIntent = intent;
              contextId = setContextResponse[index] && setContextResponse[index].context_id;
              if (contextId && setContextReqIntent) {
                // change params into objects
                const authFlowContextId = riskInfo.authflow_context_id;

                // Constructing the request for update context API call
                const articleContent = {
                  contextMetaData: {
                    context_id: contextId,
                    intent: setContextReqIntent,
                  },
                  articleId: faq,
                };

                updateContext(articleContent, '', true, authFlowContextId, stepUpRequestedTime);
              }
            });
          } else if (
            Array.isArray(setContextResponse) &&
            setContextResponse.length === 1 &&
            Array.isArray(setContextIntents) &&
            setContextIntents.length === 1
          ) {
            // If the set context Request has only one intent make the update context call after step up authentication
            contextId = setContextResponse[0].context_id;

            setContextReqIntent = setContextIntents[0];

            if (contextId && setContextReqIntent) {
              const authFlowContextId = riskInfo.authflow_context_id;

              // Constructing the request for update context API call
              const articleContent = {
                contextMetaData: {
                  context_id: contextId,
                  intent: setContextReqIntent,
                },
                articleId: faq,
              };

              updateContext(articleContent, '', true, authFlowContextId, stepUpRequestedTime);
            }
          }
        }

        dispatch(resetOcpContextResponse());

        return dispatch(
          initiateRiskCheck(stepUpFromChannel, persistedTopic, { isOpenFinalIntentBeforeStepUp: true }, 'second'),
        );
      }
    }

    if (!Page.isLoggedIn()) {
      // resetting step up state in logout
      dispatch(resetStepUp());
      dispatch(closeModalContainer());
    }

    // Show Error message, if the Lexa service is failed
    if (
      Page.isLexaPilotFailure() &&
      (fromChannel === modalChannels.liveChat ||
        fromChannel === modalChannels.chatBot ||
        fromChannel === modalChannels.defaultEmail ||
        fromChannel === modalChannels.finalEmail ||
        fromChannel === modalChannels.messageUs)
    ) {
      // Fetching final topic intent, if the topic select result is persisted
      if (!_isEmpty(topicTreeSelectResult)) {
        dispatch(fetchFinalTopicContent({ topic: topicTreeSelectResult, fromChannel }));
      }
      setTimeout(() => purgeStoredState({ storage: asyncSessionStorage }).then(() => {}), 400);
      trackPage({ id: 'contactLexaFailureError', link: 'smarthelp-intermediate|lexa-failure' });
      dispatch(loadMessageModal({ message: modalErrorMsg, channel: fromChannel, topic: topicTreeSelectResult })); // Open Prompt window and show error message
    }

    // ----------------------  POST CHAT BOT ELIGIBILITY LOGIC -------------------------------------

    // DEFAULT CONTACT CHANNEL / NOT FROM FINAL INTENT
    // POST CHAT BOT ELIGIBILITY CALL
    if (!stepUpFromChannel && !wasChatBotClicked() && !isNewContactUsExpEnabled()) {
      dispatch(chatBotEligibilityResponse());
    }

    // If request is from email Page do not execute any logic
    if (fromEmailPage) {
      return null;
    }

    // CHAT BOT PILL LOGIC FOR LOGGED OUT TO LOGIN WHEN CLICKED ON PILL IN LOGGED OUT CHAT
    // If clicked on chat bot pill - If final intent open final intent and open respective chat window
    // If not from final intent land on default home page and open respective chat window.
    if (wasChatBotClicked() && isLoggedOutToLogin(getState())) {
      if (!_isEmpty(topic)) {
        dispatch(fetchFinalTopicContent({ topic, fromChannel }));
      }
      return dispatch(chatBotEligibilityResponse({origin: asyncOrigins.CHAT_PILL}));
    }

    // ----------------------  POST CHAT BOT ELIGIBILITY LOGIC -------------------------------------

    // ------------------ CONTACT CHANNELS LOGGED OUT TO LOGGED IN LOGIC ---------------------------

    // CALL US
    if (fromChannel === modalChannels.call && isLoggedIn) {
      // default contact option callUs login.
      if (stepUpFromChannel !== modalChannels.call) {
        // COVID BCP CALL US logic from logged out to logged in
        // Default CallUs
        // NON IDI
        if (!Page.getIsIDIContactOptions()) {
          if (isSuppressCallUs()) {
            // Show COVID Callus alert window.
            dispatch(showHideCovidBcpModalAlert(true));
            dispatch(closeModalContainer());
          } else {
            dispatch(locationUpdate({ pageName: 'callUs' }));
          }
        } else if (isCallUsPartOfIdi(channels)) {
          // IDI
          // Check is callUs channel is available in IDI default channels
          // Only if channel is available in IDI response then redirect
          dispatch(locationUpdate({ pageName: 'callUs' }));
        } else {
          // Show COVID Callus alert window.
          dispatch(showHideCovidBcpModalAlert(true));
          dispatch(closeModalContainer());
        }
      }

      if (!islocaleChanged) {
        // Final intent callUs login
        if (stepUpFromChannel === modalChannels.call) {
          dispatch(initiateRiskCheck(stepUpFromChannel, persistedTopic));
        }
      }
      return null;
    }

    // CHAT/LIVE CHAT
    if ((fromChannel === modalChannels.liveChat || fromChannel === modalChannels.chat) && isLoggedIn) {
      const isOpenFinalIntent = isFinalIntent(persistedTopic);
      return dispatch(
        initiateRiskCheck(fromChannel, persistedTopic, { isOpenFinalIntentBeforeStepUp: isOpenFinalIntent }),
      );
    }

    // MESSAGE US
    if (isLoggedIn && fromChannel === modalChannels.messageUs) {
      // This condition is for the users coming from callUs related options messageUs
      // If endpoint is not null and endpoint is equal to smc then redirect the user to SMC page.
      if (!_isEmpty(endPoint) && endPoint === 'smc') {
        if (!islocaleChanged && (Page.isPilotUser() || Page.isRedirectToSMC())) {
          // Need to reset back the modal state
          dispatch(closeModalContainer());
          return Page.redirectToSMC();
        }
        // Dispatching chatbot eligiblity call from logout to login from callus page after message us click when locale changed
        dispatch(chatBotEligibilityResponse());
      } else {
        const isOpenFinalIntent = isFinalIntent(persistedTopic);
        return dispatch(
          initiateRiskCheck(fromChannel, persistedTopic, { isOpenFinalIntentBeforeStepUp: isOpenFinalIntent }),
        );
      }
      return null;
    }

    // FINAL INTENT EMAIL CONTACT CHANNEL
    if (fromChannel === modalChannels.finalEmail && isLoggedIn && !islocaleChanged) {
      return dispatch(renderFinalIntentEmailFlow());
    }

    // DEFAULT EMAIL CONTACT CHANNEL
    if (fromChannel === modalChannels.defaultEmail && isLoggedIn && !islocaleChanged) {
      setTimeout(() => purgeStoredState({ storage: asyncSessionStorage }).then(() => {}), 400);

      // Pilot user checker for login from default Email option
      if (Page.isPilotUser()) {
        // making chatcard clicked true after pilot user clicks on email icon and log in.
        setChatCardClickInSession(true);
        asyncChatResolver('GENPOOL|contactUsPage');

        // Redirect to Call us page, if the user select Email option in the call us page
        if (pageName === 'callUs') {
          dispatch(locationUpdate({ pageName: 'callUs' }));
        }
      } else if (Page.isRedirectToSMC()) {
        Page.redirectToSMC();
      } else {
        dispatch(locationUpdate({ pageName: 'email' }));
      }
    }
  };
}
